import { css } from '@emotion/css';

import { Clickable, ClickHandler } from '../../ui/Clickable';
import { isDefined } from '../../lib/MaybeV2';
import { paper } from '../paper';
import { Colors } from '../../ui/vars';
import React from 'react';
import { Icon, Icon16 } from '../../ui/icons/Icon';

interface Props {
    icon: React.ReactNode;
    disabled?: boolean;
    small?: boolean;
    link?: string | null;
    icon16px?: boolean;
    type?: IconButtonType;
    onClick: ClickHandler;
    key?: React.Key;
}

export enum IconButtonType {
    regular = 1,
    inverted,
    confirm,
    stop,
}

function getColor(type: IconButtonType): string {
    switch (type) {
        case IconButtonType.regular:
            return Colors.blue;
        case IconButtonType.inverted:
            return Colors.white;
        case IconButtonType.confirm:
            return Colors.white;
        case IconButtonType.stop:
            return Colors.white;
    }
}

function getBackgroundColor(type: IconButtonType): string {
    switch (type) {
        case IconButtonType.regular:
            return Colors.white;
        case IconButtonType.inverted:
            return Colors.blue;
        case IconButtonType.confirm:
            return Colors.green;
        case IconButtonType.stop:
            return Colors.error;
    }
}

export function IconButton({
    icon,
    disabled,
    small,
    link,
    icon16px,
    type,
    onClick,
}: Props) {
    const buttonType = type || IconButtonType.inverted;
    const color = getColor(buttonType);
    const backgroundColor = getBackgroundColor(buttonType);
    const circle = (
        <Clickable
            element="div"
            className={css([
                {
                    cursor: 'pointer',
                    position: 'absolute',
                    borderRadius: '100%',
                    ...paper(2),
                    background: backgroundColor,
                    color: color,
                },
                disabled && {
                    cursor: 'default',
                    opacity: 0.4,
                },
                small && {
                    top: '12px',
                    left: '12px',
                    width: '24px',
                    height: '24px',
                },
                !small && {
                    top: '4px',
                    left: '4px',
                    width: '40px',
                    height: '40px',
                    padding: '8px',
                },
                icon16px && {
                    paddingTop: '4px',
                    paddingLeft: '4px',
                },
            ])}
            disabled={disabled || false}
            onClick={onClick}
        >
            {icon16px ? <Icon16 icon={icon} /> : <Icon icon={icon} />}
        </Clickable>
    );

    if (isDefined(link)) {
        return (
            <a
                className={css({
                    textDecoration: 'none',
                    display: 'inline-block',
                    position: 'relative',
                    width: '48px',
                    height: '48px',
                })}
                tabIndex={-1}
                target={link ? '_blank' : undefined}
                href={link}
                rel="noreferrer"
            >
                {circle}
            </a>
        );
    } else {
        return (
            <div
                className={css({
                    position: 'relative',
                    width: '48px',
                    height: '48px',
                })}
            >
                {circle}
            </div>
        );
    }
}
